'use client';

import { servicesGalleryPhotos } from '@/constants';
import { cn } from '@/lib/utils';
import { PathEnum } from '@/types/enums';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Link from '../ui/Link';

const PHOTOS_PER_PAGE_DESKTOP = 4;
const PHOTOS_PER_PAGE_MOBILE = 2;
const FADE_DURATION_MS = 600;

function isSquarePhoto(globalIndex: number): boolean {
    if (globalIndex === 0) return true;
    if (globalIndex < 3) return false;
    const offset = globalIndex - 3;
    return offset % 4 === 0 || offset % 4 === 1;
}

function preloadImage(src: string): Promise<void> {
    return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

export default function ServicesGallerySection() {
    const t = useTranslations('Services');
    const photos = useMemo(() => servicesGalleryPhotos(t), [t]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isAnimating, setIsAnimating] = useState<boolean>(false);

    const totalPhotos = photos.length;

    const navigate = useCallback(
        async (photosPerPage: number, direction: 'next' | 'prev') => {
            if (isAnimating) return;

            const nextIndex =
                direction === 'next'
                    ? currentIndex + photosPerPage >= totalPhotos
                        ? 0
                        : currentIndex + photosPerPage
                    : currentIndex === 0
                      ? totalPhotos - photosPerPage
                      : currentIndex - photosPerPage;

            setIsAnimating(true);

            const imagesToLoad = photos
                .slice(nextIndex, nextIndex + photosPerPage)
                .map(({ imageSrc }) => imageSrc);

            await Promise.all([
                ...imagesToLoad.map(preloadImage),
                new Promise<void>((resolve) => setTimeout(resolve, FADE_DURATION_MS)),
            ]);

            setCurrentIndex(nextIndex);
            setIsAnimating(false);
        },
        [currentIndex, isAnimating, photos, totalPhotos],
    );

    useEffect(() => {
        const nextStart = PHOTOS_PER_PAGE_DESKTOP;
        const prevStart =
            totalPhotos > PHOTOS_PER_PAGE_DESKTOP ? totalPhotos - PHOTOS_PER_PAGE_DESKTOP : 0;

        [nextStart, prevStart].forEach((start) => {
            photos
                .slice(start, start + PHOTOS_PER_PAGE_DESKTOP)
                .forEach(({ imageSrc }) => preloadImage(imageSrc));
        });
    }, [photos, totalPhotos]);

    return (
        <section className="w-full bg-dark-grey relative py-12 lg:py-16 2xl:py-20">
            <div className="container mx-auto px-0 max-lg:px-4 flex flex-col max-lg:flex-col-reverse justify-between lg:gap-12 xl:gap-16 2xl:gap-20 gap-[60px]">
                <Gallery
                    photos={photos}
                    currentIndex={currentIndex}
                    photosPerPageDesktop={PHOTOS_PER_PAGE_DESKTOP}
                    photosPerPageMobile={PHOTOS_PER_PAGE_MOBILE}
                    onPreviousClick={() => navigate(PHOTOS_PER_PAGE_MOBILE, 'prev')}
                    onNextClick={() => navigate(PHOTOS_PER_PAGE_MOBILE, 'next')}
                    isAnimating={isAnimating}
                />
                <div className="w-full flex justify-between items-center gap-6 xl:gap-16 2xl:gap-[190px] min-w-0">
                    <ArrowButton
                        size={64}
                        className="max-lg:hidden shrink-0 2xl:[&_svg]:!w-[86px] 2xl:[&_svg]:!h-[86px]"
                        direction="left"
                        onClick={() => navigate(PHOTOS_PER_PAGE_DESKTOP, 'prev')}
                    />
                    <Info />
                    <ArrowButton
                        size={64}
                        className="max-lg:hidden shrink-0 2xl:[&_svg]:!w-[86px] 2xl:[&_svg]:!h-[86px]"
                        direction="right"
                        onClick={() => navigate(PHOTOS_PER_PAGE_DESKTOP, 'next')}
                    />
                </div>
            </div>
        </section>
    );
}

const Gallery = ({
    photos,
    currentIndex,
    photosPerPageDesktop,
    photosPerPageMobile,
    onPreviousClick,
    onNextClick,
    isAnimating,
}: {
    photos: { imageSrc: string; title: string }[];
    currentIndex: number;
    photosPerPageDesktop: number;
    photosPerPageMobile: number;
    onPreviousClick: () => void;
    onNextClick: () => void;
    isAnimating: boolean;
}) => {
    return (
        <div className="w-full flex flex-col min-w-0">
            <div className="hidden lg:grid lg:grid-cols-3 lg:gap-8 xl:gap-10 2xl:gap-[60px] w-full">
                {photos
                    .slice(currentIndex, currentIndex + photosPerPageDesktop)
                    .map(({ title, imageSrc }, index) => {
                        const globalIndex = currentIndex + index;

                        return (
                            <GalleryImage
                                key={globalIndex}
                                title={title}
                                imageSrc={imageSrc}
                                isSquare={isSquarePhoto(globalIndex)}
                                isAnimating={isAnimating}
                                index={index}
                                priority={globalIndex < photosPerPageDesktop}
                            />
                        );
                    })}
            </div>
            <div className="flex flex-col gap-[30px] lg:hidden">
                {photos
                    .slice(currentIndex, currentIndex + photosPerPageMobile)
                    .map(({ title, imageSrc }, index) => {
                        const globalIndex = currentIndex + index;

                        return (
                            <GalleryImage
                                key={globalIndex}
                                title={title}
                                imageSrc={imageSrc}
                                isAnimating={isAnimating}
                                index={index}
                                priority={globalIndex < photosPerPageMobile}
                            />
                        );
                    })}
            </div>
            <div className="flex items-center justify-between mt-7 lg:hidden">
                <ArrowButton size={50} direction="left" onClick={onPreviousClick} />
                <ArrowButton size={50} direction="right" onClick={onNextClick} />
            </div>
        </div>
    );
};

const GalleryImage = ({
    title,
    imageSrc,
    isSquare = false,
    isAnimating,
    index,
    priority = false,
}: {
    title: string;
    imageSrc: string;
    isSquare?: boolean;
    isAnimating: boolean;
    index: number;
    priority?: boolean;
}) => {
    const sequenceNumber = index + 1;
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoaded(false);

        const img = new window.Image();
        img.src = imageSrc;

        if (img.complete) {
            setIsLoaded(true);
            return;
        }

        img.onload = () => {
            if (!cancelled) setIsLoaded(true);
        };

        return () => {
            cancelled = true;
        };
    }, [imageSrc]);

    const isVisible = !isAnimating && isLoaded;

    return (
        <div
            className={cn(
                'group relative col-span-2 h-[280px] xl:h-[320px] 2xl:h-[400px] max-lg:col-span-full max-lg:w-full max-lg:h-[320px] max-sm:h-[240px] transition-opacity overflow-hidden',
                isSquare && 'col-span-1',
                isVisible ? 'opacity-100' : 'opacity-0',
            )}
            style={{ transitionDuration: `${sequenceNumber * 0.2}s` }}
        >
            <Image
                className="object-cover"
                src={imageSrc}
                alt={title}
                fill
                priority={priority}
                sizes="(max-width: 1024px) 100vw, (max-width: 1536px) 33vw, 400px"
            />
            {title ? (
                <span className="text-white absolute bottom-2 left-4 text-h4 lg:text-h3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-1">
                    {title}
                </span>
            ) : null}
            <div
                className="h-full w-full absolute top-0 left-0 opacity-0 group-hover:opacity-100 duration-300 transition-opacity pointer-events-none"
                style={{
                    background:
                        'linear-gradient(180deg, rgba(34, 48, 53, 0) 0%, rgba(34, 48, 53, 0.29603) 66.5%, rgba(34, 48, 53, 0.74) 99.99%, #223035 100%)',
                }}
            />
        </div>
    );
};

const Info = () => {
    const t = useTranslations();

    return (
        <div className="flex flex-col gap-7 sm:gap-5 text-white min-w-0">
            <h2 className="text-h2 max-lg:text-h3 leading-tight font-medium">
                {t('AllServices.anInsight')}
            </h2>
            <div className="flex max-lg:flex-col max-lg:items-start items-center lg:gap-10 xl:gap-16 2xl:gap-20 gap-10">
                <p className="text-h5 font-normal">{t('AllServices.withEngineeringServices')}</p>
                <Link className="get-offer-trigger w-fit shrink-0" href={PathEnum.Contact}>
                    <Button
                        title={t('Buttons.getOffer')}
                        withArrow
                        variant="secondary"
                        className="text-white w-max border-white"
                    />
                </Link>
            </div>
        </div>
    );
};

const ArrowButton = ({
    size,
    direction,
    onClick,
    className,
}: {
    size: number;
    direction: 'left' | 'right';
    onClick: () => void;
    className?: string;
}) => {
    const Icon = direction === 'left' ? ArrowLeft : ArrowRight;

    return (
        <button className={className} type="button" aria-label={direction} onClick={onClick}>
            <Icon
                strokeWidth={1}
                width={size}
                height={size}
                className="hover:text-brand transition-colors duration-300 ease-in-out text-white"
            />
        </button>
    );
};
